/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../../../common/constants';
import { normalizeRubricsDetail, normalizeRubricsListResponse } from '../../components/rubrics/rubricsUtils';

const RubricsData = types
  .model('RubricsData', {
    evaluations: types.optional(types.frozen(), []),
    availableEvalTypes: types.optional(types.frozen(), []),
    listGraphId: types.optional(types.string, ''),
    listTenantId: types.optional(types.string, ''),
    loadError: types.optional(types.boolean, false),
    createdRubricId: types.optional(types.string, ''),
    detail: types.optional(types.frozen(), null),
    detailError: types.optional(types.boolean, false),
    view: types.optional(types.frozen(), null),
    isListStale: types.optional(types.boolean, false)
  })
  .actions((self) => ({
    fetchList({ tenantId, graphId }) {
      applySnapshot(self, {
        ...getSnapshot(self),
        listGraphId: graphId ?? '',
        listTenantId: tenantId ?? '',
        loadError: false
      });
      Framework.getEventManager().publish(constants.GET_RUBRICS_LIST, {
        tenantId,
        graphId
      });
    },
    setListResponse(response) {
      const { evaluations, availableEvalTypes } = normalizeRubricsListResponse(response);
      applySnapshot(self, {
        ...getSnapshot(self),
        evaluations,
        availableEvalTypes,
        loadError: false,
        isListStale: false
      });
    },
    failListRequest() {
      applySnapshot(self, {
        ...getSnapshot(self),
        evaluations: [],
        availableEvalTypes: [],
        listGraphId: '',
        listTenantId: '',
        loadError: true
      });
    },
    deleteRubric({ tenantId, graphId, rubricId }) {
      Framework.getEventManager().publish(constants.DELETE_RUBRICS_RUBRIC, {
        tenantId,
        graphId,
        rubricId
      });
    },
    completeDelete() {
      Framework.getEventManager().publish(constants.SET_DELETE_RUBRICS_RUBRIC, {
        success: true
      });
    },
    failDelete() {
      Framework.getEventManager().publish(constants.SET_DELETE_RUBRICS_RUBRIC, {
        success: false
      });
    },
    createRubric({ tenantId, graphId, requestBody }) {
      Framework.getEventManager().publish(constants.CREATE_RUBRICS_RUBRIC, {
        tenantId,
        graphId,
        requestBody
      });
    },
    completeCreate(body) {
      const rubricId = body?.rubricId != null ? String(body.rubricId) : '';
      applySnapshot(self, {
        ...getSnapshot(self),
        createdRubricId: rubricId,
        isListStale: true
      });
      Framework.getEventManager().publish(constants.SET_CREATE_RUBRICS_RUBRIC, {
        success: true,
        rubricId
      });
    },
    failCreate() {
      Framework.getEventManager().publish(constants.SET_CREATE_RUBRICS_RUBRIC, {
        success: false
      });
    },
    saveScheduler({ tenantId, graphId, rubricId, requestBody }) {
      Framework.getEventManager().publish(constants.SAVE_RUBRICS_SCHEDULER, {
        tenantId,
        graphId,
        rubricId,
        requestBody
      });
    },
    completeScheduler() {
      applySnapshot(self, {
        ...getSnapshot(self),
        isListStale: true
      });
      Framework.getEventManager().publish(constants.SET_SAVE_RUBRICS_SCHEDULER, {
        success: true
      });
    },
    failScheduler() {
      Framework.getEventManager().publish(constants.SET_SAVE_RUBRICS_SCHEDULER, {
        success: false
      });
    },
    fetchDetail({ tenantId, graphId, rubricId }) {
      applySnapshot(self, {
        ...getSnapshot(self),
        detail: null,
        detailError: false
      });
      Framework.getEventManager().publish(constants.GET_RUBRICS_DETAIL, {
        tenantId,
        graphId,
        rubricId
      });
    },
    setDetailResponse(response) {
      applySnapshot(self, {
        ...getSnapshot(self),
        detail: normalizeRubricsDetail(response),
        detailError: false
      });
    },
    failDetailRequest() {
      applySnapshot(self, {
        ...getSnapshot(self),
        detail: null,
        detailError: true
      });
    },
    updateRubric({ tenantId, graphId, rubricId, requestBody }) {
      Framework.getEventManager().publish(constants.UPDATE_RUBRICS_RUBRIC, {
        tenantId,
        graphId,
        rubricId,
        requestBody
      });
    },
    markListStale() {
      applySnapshot(self, {
        ...getSnapshot(self),
        isListStale: true
      });
    },
    completeUpdate() {
      applySnapshot(self, {
        ...getSnapshot(self),
        isListStale: true
      });
      Framework.getEventManager().publish(constants.SET_UPDATE_RUBRICS_RUBRIC, {
        success: true
      });
    },
    failUpdate() {
      Framework.getEventManager().publish(constants.SET_UPDATE_RUBRICS_RUBRIC, {
        success: false
      });
    },
    setView(view) {
      applySnapshot(self, {
        ...getSnapshot(self),
        view: view ?? null
      });
    },
    clearListData() {
      applySnapshot(self, {
        ...getSnapshot(self),
        evaluations: [],
        availableEvalTypes: [],
        listGraphId: '',
        listTenantId: '',
        loadError: false,
        isListStale: false
      });
    },
    clearFormState() {
      applySnapshot(self, {
        ...getSnapshot(self),
        createdRubricId: '',
        detail: null,
        detailError: false
      });
    },
    clearAll() {
      applySnapshot(self, {
        ...getSnapshot(self),
        evaluations: [],
        availableEvalTypes: [],
        listGraphId: '',
        listTenantId: '',
        loadError: false,
        createdRubricId: '',
        detail: null,
        detailError: false,
        view: null,
        isListStale: false
      });
    }
  }));
export default RubricsData;
